// =============================================================================
// useFeatures — read this rooftop's Recon feature flags.
// -----------------------------------------------------------------------------
// Group admins flip toggles in portal.sevric.com/store-settings under
// `apps.recon.features`. This hook subscribes to the active store doc and
// returns a merged-with-defaults feature map.
//
//   const features = useFeatures();
//   if (features.triage) <NavLink to="/triage" />
//
//   <RequireFeature name="vendors"><Vendors /></RequireFeature>
//
// First-time hydration (before snapshot lands) returns the defaults — every
// feature ON — so we never flash a hidden nav while waiting for Firestore.
// Disabled features hide nav links and redirect direct URL access to /inspect
// (the safe default for techs without manager access).
// =============================================================================

import { createElement, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase.js";
import { useAuth } from "../auth/AuthProvider.jsx";

export const RECON_FEATURE_DEFAULTS = {
  // Pages
  today: true,
  pipeline: true,
  inspect: true,
  triage: true,
  vendors: true,
  reports: true,
  salespersonView: true,
  chat: true,
  // Sub-features
  find: true,
  vendorPortal: true,
  photoRequired: false,
  aiInsights: true,
  bulkActions: true,
  kickbacks: true,
  exports: true,
};

export function useFeatures() {
  const { profile } = useAuth();
  const storeId = profile?.storeId || null;
  const [features, setFeatures] = useState(RECON_FEATURE_DEFAULTS);

  useEffect(() => {
    if (!storeId) return undefined;
    const unsub = onSnapshot(
      doc(db, "stores", storeId),
      (snap) => {
        const f = snap.data()?.apps?.recon?.features || {};
        setFeatures({ ...RECON_FEATURE_DEFAULTS, ...f });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn("[useFeatures] store read failed:", err?.message);
      }
    );
    return unsub;
  }, [storeId]);

  return features;
}

export function useFeature(name) {
  const features = useFeatures();
  if (!(name in features)) return true;
  return features[name] !== false;
}

// Implemented with createElement so this file can stay .js (Vite + esbuild
// only treat .jsx as JSX by default in this project).
export function RequireFeature({ name, children, fallback = "/inspect" }) {
  const enabled = useFeature(name);
  if (!enabled) return createElement(Navigate, { to: fallback, replace: true });
  return children;
}
